"use client"

import {
  CenterAligned,
  FlexColumn,
  Modal,
  ModalBody,
  ModalHeaderTitle,
  TextBody,
  TextDisplay,
  useToasts,
} from "@opensea/ui-kit"
import { useEffect, useRef, useState } from "react"
import { useTranslations } from "use-intl"
import { useSnapshot } from "valtio"
import { AnimatedLogo } from "../../components/AnimatedLogo"
import { Button } from "../../components/Button"
import { useAddress, useDisconnect, useIsReady } from "../../polychain/hooks"
import { useHasAcceptedTerms } from "../../polychain/hooks/useHasAcceptedTerms"
import { useIsAuthenticated } from "../../polychain/hooks/useIsAuthenticated"
import { useFlag } from "../../providers/FlagProvider"
import { useSiwe } from "../../providers/SiweProvider"
import { useTrackError } from "../../providers/TrackErrorProvider"
import { FlowActions, FlowState } from "../../stores/flowStore"
import type { FlowStateVariants } from "../flow-state"

export const AuthenticateFlow = () => {
  const t = useTranslations("wallet.AuthenticateFlow")
  const { flow: currentFlow } = useSnapshot(FlowState)

  const address = useAddress()
  const ready = useIsReady()
  const connected = ready && Boolean(address)
  const authenticated = useIsAuthenticated()
  const hasAcceptedTerms = useHasAcceptedTerms()
  const acceptTermsEnabled = useFlag("acceptTermsEnabled")

  const { promptSiwe, isSiwePending } = useSiwe()
  const { disconnect } = useDisconnect()
  const { showErrorMessage } = useToasts()
  const trackError = useTrackError()

  const [previousFlow, setPreviousFlow] = useState<
    FlowStateVariants | undefined
  >(undefined)

  const authenticateFlow =
    currentFlow?.type === "authenticate" ? currentFlow : undefined

  const onAuthenticate = async () => {
    try {
      await promptSiwe()
      FlowActions.onSuccess()
      FlowActions.endFlow()
      authenticateFlow?.onAuthenticated?.()
      if (previousFlow) {
        FlowActions.startFlow(previousFlow)
      }
    } catch (error) {
      trackError(error as Error)
      showErrorMessage(t("errorMessage"))
    }
  }

  const open =
    connected &&
    authenticated === false &&
    (hasAcceptedTerms === true || acceptTermsEnabled === false)

  // Elevate this flow above others when needed
  useEffect(() => {
    if (
      open &&
      // These flows should not be interrupted
      currentFlow?.type !== "accept-terms" &&
      currentFlow?.type !== "authenticate" &&
      currentFlow?.type !== "connect"
    ) {
      if (currentFlow) {
        setPreviousFlow(currentFlow as FlowStateVariants)
      }
      FlowActions.startFlow({ type: "authenticate" })
    }
  }, [currentFlow, open])

  const hasAutoPromptedSiwe = useRef(false)
  useEffect(() => {
    const autoPromptSiwe =
      authenticateFlow?.autoPromptSiwe && !hasAutoPromptedSiwe.current
    if (open) {
      if (autoPromptSiwe) {
        void onAuthenticate()
        hasAutoPromptedSiwe.current = true
      }
    } else {
      hasAutoPromptedSiwe.current = false
    }
  }, [open, onAuthenticate, authenticateFlow])

  return (
    <Modal
      className="md:rounded-xl"
      content={
        <ModalBody className="pt-12 pb-6">
          <CenterAligned className="mb-6">
            <AnimatedLogo />
          </CenterAligned>
          <TextDisplay asChild className="mb-8 text-center" size="sm">
            <ModalHeaderTitle>{t("title")}</ModalHeaderTitle>
          </TextDisplay>
          <FlexColumn className="gap-4">
            <TextBody className="text-center" color="text-secondary" size="sm">
              {t("description")}
            </TextBody>
            <Button
              autoFocus
              disabled={isSiwePending}
              isLoading={isSiwePending}
              onClick={onAuthenticate}
              size="lg"
            >
              {t("authenticate")}
            </Button>
          </FlexColumn>
        </ModalBody>
      }
      onOpenChange={openState => {
        if (!openState) {
          if (!(authenticated || isSiwePending)) {
            disconnect()
          }
          FlowActions.endFlow()
        }
      }}
      open={open}
      size="sm"
      withCloseIcon={false}
    />
  )
}
